import { Injectable } from '@angular/core'
import { LibrarySidebarTab } from '@app/library-module/pages/library-file-page/library-file.page'
import { IGetUploadResponse, IScreenshot, IUploadTag } from '@app/recorder-module/core/types/upload'
import { ISelectOption } from '@app/shared/components/select-popover'
import { Palette } from '@app/shared/helpers/palette'
import { BehaviorSubject, distinctUntilChanged, filter, Observable, Subject } from 'rxjs'
import { ChangeUploadTitleCommand } from '../commands/change-upload-title.command'
import { CurrentOrganization } from '@app/shared/services/current-organization'
import {
  EMPTY_LIBRARY_PRIVATE_ACCESS_PERMISSIONS,
  ILibraryPrivateAccessPermissions,
} from '@app/library-module/core/types/upload-access'

@Injectable({
  providedIn: 'root',
})
export class LibraryFileService {
  private readonly _uuid$: BehaviorSubject<null | string> = new BehaviorSubject<null | string>(null)
  private readonly _record$: BehaviorSubject<null | IGetUploadResponse> =
    new BehaviorSubject<null | IGetUploadResponse>(null)
  private readonly _screenshot$: BehaviorSubject<null | IScreenshot> = new BehaviorSubject<null | IScreenshot>(null)
  private readonly _permissions$: BehaviorSubject<ILibraryPrivateAccessPermissions> =
    new BehaviorSubject<ILibraryPrivateAccessPermissions>(EMPTY_LIBRARY_PRIVATE_ACCESS_PERMISSIONS)

  private readonly _updateAllData$: Subject<boolean> = new Subject<boolean>()
  private readonly _updateData$: Subject<boolean> = new Subject<boolean>()
  private readonly _updateLimits$: Subject<boolean> = new Subject<boolean>()

  private readonly _scrollEnd$: Subject<boolean> = new Subject<boolean>()

  private readonly _resetTranscriptionStorage$: Subject<boolean> = new Subject<boolean>()
  private readonly _displayTranscriptionState$: Subject<boolean> = new Subject<boolean>()

  private readonly _isShowLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  private switchTabSubject = new Subject<LibrarySidebarTab>()
  switchTab$ = this.switchTabSubject.asObservable()

  constructor(
    private currentOrganization: CurrentOrganization,
    private changeUploadTitleCommand: ChangeUploadTitleCommand
  ) {}

  set uuid(value: string) {
    this._uuid$.next(value)
  }

  get uuid$(): Observable<null | string> {
    return this._uuid$.pipe(filter((value) => !!value))
  }

  get uuid(): null | string {
    return this._uuid$.value
  }

  set record(value: IGetUploadResponse) {
    this._record$.next(value)
  }

  set screenshot(value: IScreenshot) {
    this._screenshot$.next(value)
  }

  get record$(): Observable<IGetUploadResponse | null> {
    // TODO: resolve change detection loop
    return this._record$.pipe(
      filter(Boolean),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
    )
  }

  get screenshot(): null | IScreenshot {
    return this._screenshot$.value
  }

  get record(): null | IGetUploadResponse {
    return this._record$.value
  }

  set permissions(value: ILibraryPrivateAccessPermissions) {
    this._permissions$.next(value)
  }

  get permissions$(): Observable<ILibraryPrivateAccessPermissions> {
    return this._permissions$
  }

  get permissions(): ILibraryPrivateAccessPermissions {
    return this._permissions$.value
  }

  updateAllData(): void {
    this._updateAllData$.next(true)
  }

  get updateAllData$(): Observable<boolean> {
    return this._updateAllData$
  }

  updateData(): void {
    this._updateData$.next(true)
  }

  get updateData$(): Observable<boolean> {
    return this._updateData$
  }

  updateLimits(): void {
    this._updateLimits$.next(true)
  }

  get updateLimits$(): Observable<boolean> {
    return this._updateLimits$
  }

  onScrollEnd(): void {
    this._scrollEnd$.next(true)
  }

  get onScrollEnd$(): Observable<boolean> {
    return this._scrollEnd$
  }

  resetTranscriptionStorage(): void {
    this._resetTranscriptionStorage$.next(true)
  }

  get resetTranscriptionStorage$(): Observable<boolean> {
    return this._resetTranscriptionStorage$
  }

  displayTranscriptionState(state: boolean): void {
    this._displayTranscriptionState$.next(state)
  }

  get displayTranscriptionState$(): Observable<boolean> {
    return this._displayTranscriptionState$
  }

  set isShowLoading(value: boolean) {
    this._isShowLoading$.next(value)
  }

  get isShowLoading$(): Observable<boolean> {
    return this._isShowLoading$
  }

  get isShowLoading(): boolean {
    return this._isShowLoading$.value
  }

  buildTagOptions(tagNames: string[], allTags: IUploadTag[]): ISelectOption[] {
    return tagNames
      .map((name) => allTags.find((t) => t.name === name))
      .filter((r) => !!r)
      .map((tag) => {
        return {
          id: tag.id,
          label: tag.name,
          value: tag.id?.toString()!,
          color: tag.color != null ? Palette.common[tag.color] : null,
          extendedData: {
            colorId: tag.color,
          },
        }
      })
  }

  switchToTab(tab: LibrarySidebarTab): void {
    this.switchTabSubject.next(tab)
  }

  changeTitle(fileUuid: string, title: string): Observable<void> {
    return this.changeUploadTitleCommand.call(this.currentOrganization.id, fileUuid, title)
  }
}
